import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectAuthData,
  selectCallbackRemindersEnabled,
  selectExtraRoutesAuthData,
} from '@/containers/Login/AuthSelectors'
import { clearAuth, updateCallbackRemindersEnabled } from '@/containers/Login/AuthSlice'
import { modulesConfig } from '@/containers/SubscriptionFormPageNew/config'
import getAPIConfig from '@/shared/tools/getAPIConfig'
import { ipNotAllowedActions } from '@/shared/tools/ipNotAllowed'

export const CALLBACK_REMINDERS_POLL_MS = 60000

type FetchInit = Parameters<typeof fetch>[1]

const HTTP_UNAUTHORIZED = 401
const HTTP_FORBIDDEN = 403

export type CallbackReminderId = string | number

export interface CallbackReminder {
  id: CallbackReminderId
  contact_first_name?: string | null
  contact_last_name?: string | null
  contact_phone?: string | null
  zoho_contact_id?: string | null
  scheduled_at: string
}

interface AuthData {
  authTokenSession?: string
  country?: string
}

interface ReminderRoutes {
  scheduleCallbackRemindersUrl?: string
  scheduleCallbackDismissReminderUrl?: string
}

interface RemindersBody {
  data?: unknown
  meta?: { reminders_enabled?: unknown }
}

interface UseCallbackRemindersReturn {
  reminders: CallbackReminder[]
  dismissingIds: ReadonlySet<CallbackReminderId>
  dismiss: (id: CallbackReminderId) => Promise<void>
}

const isReminder = (value: unknown): value is CallbackReminder =>
  typeof value === 'object' &&
  value !== null &&
  'id' in value &&
  'scheduled_at' in value &&
  typeof value.scheduled_at === 'string' &&
  Number.isFinite(Date.parse(value.scheduled_at))

const readJson = (response: Response): Promise<unknown> => response.json().catch(() => null)

const withoutId = (ids: ReadonlySet<CallbackReminderId>, id: CallbackReminderId): ReadonlySet<CallbackReminderId> => {
  const next = new Set(ids)
  next.delete(id)
  return next
}

const useCallbackReminders = (): UseCallbackRemindersReturn => {
  const dispatch = useDispatch()
  const { authTokenSession, country } = useSelector(selectAuthData) as AuthData
  const { scheduleCallbackRemindersUrl, scheduleCallbackDismissReminderUrl } = useSelector(
    selectExtraRoutesAuthData,
  ) as ReminderRoutes
  const remindersEnabled = useSelector(selectCallbackRemindersEnabled) as boolean
  const [reminders, setReminders] = useState<CallbackReminder[]>([])
  const [dismissingIds, setDismissingIds] = useState<ReadonlySet<CallbackReminderId>>(new Set())
  const [refusedToken, setRefusedToken] = useState<string | null>(null)
  const dismissingRef = useRef(new Set<CallbackReminderId>())
  const pollSequenceRef = useRef(0)
  const dismissedAtSequenceRef = useRef(new Map<CallbackReminderId, number>())
  const countryCode = String(country || '').toLowerCase()
  const isReminderCountry =
    (modulesConfig as Record<string, { menu?: { callbackReminders?: boolean } }>)[countryCode]?.menu
      ?.callbackReminders ?? false

  const handleRefusal = useCallback(
    async (response: Response): Promise<boolean> => {
      if (response.status === HTTP_UNAUTHORIZED) {
        setRefusedToken(authTokenSession ?? null)
        dispatch(clearAuth())
        return true
      }
      if (response.status !== HTTP_FORBIDDEN) return false

      const ipRefusalActions = ipNotAllowedActions(response.status, await readJson(response))
      if (ipRefusalActions === null) return false

      setRefusedToken(authTokenSession ?? null)
      ipRefusalActions.forEach(action => dispatch(action))
      return true
    },
    [dispatch, authTokenSession],
  )

  useEffect(() => {
    if (
      refusedToken === authTokenSession ||
      !remindersEnabled ||
      !isReminderCountry ||
      !authTokenSession ||
      !scheduleCallbackRemindersUrl
    )
      return undefined

    let controller: AbortController | null = null
    let cancelled = false

    const load = async () => {
      if (controller !== null) return

      pollSequenceRef.current += 1
      const sequence = pollSequenceRef.current
      const current = new AbortController()
      controller = current

      try {
        const response = await fetch(scheduleCallbackRemindersUrl, {
          ...(getAPIConfig({ method: 'GET', authTokenSession, body: null }) as FetchInit),
          signal: current.signal,
        })
        if (cancelled || (await handleRefusal(response)) || !response.ok) return

        const body = (await response.json()) as RemindersBody | null
        if (cancelled) return

        if (!Array.isArray(body?.data)) return

        const fetched = body.data.filter(isReminder)
        const dismissedSinceStart = dismissedAtSequenceRef.current
        dispatch({ type: updateCallbackRemindersEnabled.type, payload: body?.meta?.reminders_enabled === true })
        const fresh = fetched.filter(item => (dismissedSinceStart.get(item.id) ?? 0) < sequence)
        setReminders(fresh)
      } catch {
        // Network error or abort: the next tick tries again.
      } finally {
        if (controller === current) controller = null
      }
    }

    const loadWhenVisible = () => {
      if (document.visibilityState === 'visible') load()
    }

    load()
    const interval = setInterval(loadWhenVisible, CALLBACK_REMINDERS_POLL_MS)
    document.addEventListener('visibilitychange', loadWhenVisible)

    return () => {
      cancelled = true
      clearInterval(interval)
      document.removeEventListener('visibilitychange', loadWhenVisible)
      controller?.abort()
    }
  }, [
    dispatch,
    handleRefusal,
    refusedToken,
    remindersEnabled,
    isReminderCountry,
    authTokenSession,
    scheduleCallbackRemindersUrl,
  ])

  const dismiss = useCallback(
    async (id: CallbackReminderId) => {
      if (dismissingRef.current.has(id)) return

      dismissingRef.current.add(id)
      setDismissingIds(current => new Set(current).add(id))
      try {
        if (scheduleCallbackDismissReminderUrl) {
          const response = await fetch(
            scheduleCallbackDismissReminderUrl.replace('*', String(id)),
            getAPIConfig({ method: 'POST', authTokenSession, body: null }) as FetchInit,
          )
          if (response.ok) dismissedAtSequenceRef.current.set(id, pollSequenceRef.current)
          else await handleRefusal(response)
        }
      } catch {
        // Not dismissed on the server: the next poll shows it again.
      } finally {
        setReminders(current => current.filter(item => item.id !== id))
        setDismissingIds(current => withoutId(current, id))
        dismissingRef.current.delete(id)
      }
    },
    [authTokenSession, handleRefusal, scheduleCallbackDismissReminderUrl],
  )

  return { reminders, dismissingIds, dismiss }
}

export default useCallbackReminders
